import { useEffect, useRef, useState } from 'react';

const R = 100;
// 4-point sparkle: sharp corners, deeply concave curved sides.
const starPath = `M 0 ${-R} Q 0 0 ${R} 0 Q 0 0 0 ${R} Q 0 0 ${-R} 0 Q 0 0 0 ${-R} Z`;

const OVERLAY_COLOR = '#050510';
const GLOW_INNER = '#c4b5fd';
const GLOW_OUTER = '#6d28d9';

// Four satellite stars evenly placed at the four corners around the big center star.
const SATELLITES = [
  { angle: 225, radiusFactor: 0.42, s: 0.55, spinSpeed: 1.0, tilt: 0 },   // top-left
  { angle: 315, radiusFactor: 0.42, s: 0.55, spinSpeed: 1.0, tilt: 0 },   // top-right
  { angle: 135, radiusFactor: 0.42, s: 0.55, spinSpeed: 1.0, tilt: 0 },   // bottom-left
  { angle: 45,  radiusFactor: 0.42, s: 0.55, spinSpeed: 1.0, tilt: 0 },   // bottom-right
];

const ORBIT_SPEED_DEG_PER_SEC = 6;

type Props = {
  progress: number; // 0..1
};

export default function LoadingOverlay({ progress }: Props) {
  const [dims, setDims] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1440,
    h: typeof window !== 'undefined' ? window.innerHeight : 900,
  }));

  const { w, h } = dims;
  const cx = w / 2;
  const cy = h / 2;
  const minSide = Math.min(w, h);
  const initR = minSide * 0.24;
  const initScale = initR / R;
  const finalR = Math.hypot(w, h) * 1.1;
  const finalScale = finalR / R;

  const satBaseR = minSide * 0.075;
  const satBaseScale = satBaseR / R;
  const orbitR = minSide;

  const [idleRot, setIdleRot] = useState(0);
  const [pulse, setPulse] = useState(1);
  const [idleTime, setIdleTime] = useState(0);

  useEffect(() => {
    const upd = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);

  const rafRef = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const loop = (now: number) => {
      const t = (now - start) / 1000;
      setIdleRot((t * 14) % 360);
      setPulse(1 + Math.sin(t * 1.9) * 0.05);
      setIdleTime(t);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const p = Math.max(0, Math.min(1, progress));
  const eased = 1 - Math.pow(1 - p, 3);
  const centerScale = (initScale + (finalScale - initScale) * eased) * (1 + (pulse - 1) * (1 - p));
  const rotation = idleRot + 540 * p;

  const satGrowth = 1 + eased * 1.4;
  const satRotation = idleRot * 0.6 + 360 * p;
  const orbitAngleBase = idleTime * ORBIT_SPEED_DEG_PER_SEC + p * 90;

  const fadeStart = 0.9;
  const overlayOpacity = p < fadeStart ? 1 : Math.max(0, 1 - (p - fadeStart) / (1 - fadeStart));

  const centerTransform = `translate(${cx} ${cy}) rotate(${rotation}) scale(${centerScale})`;

  const satTransform = (s: typeof SATELLITES[number]) => {
    const orbitAngle = (s.angle + orbitAngleBase * s.spinSpeed) * (Math.PI / 180);
    const sx = cx + Math.cos(orbitAngle) * orbitR * s.radiusFactor;
    const sy = cy + Math.sin(orbitAngle) * orbitR * s.radiusFactor;
    return `translate(${sx} ${sy}) rotate(${satRotation + s.tilt}) scale(${satBaseScale * s.s * satGrowth})`;
  };

  return (
    <div
      className="fixed inset-0 z-[100] select-none"
      style={{
        opacity: overlayOpacity,
        pointerEvents: overlayOpacity <= 0.02 ? 'none' : 'auto',
        transition: 'opacity 120ms linear',
      }}
    >
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        xmlns="http://www.w3.org/2000/svg"
        className="block"
      >
        <defs>
          <mask id="lo-star-mask" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width={w} height={h} fill="white" />
            <g transform={centerTransform}>
              <path d={starPath} fill="black" />
            </g>
            {SATELLITES.map((s, i) => (
              <g key={i} transform={satTransform(s)}>
                <path d={starPath} fill="black" />
              </g>
            ))}
          </mask>

          <filter id="lo-halo-inner" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
          <filter id="lo-halo-outer" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="34" />
          </filter>
        </defs>

        <g mask="url(#lo-star-mask)">
          <rect x="0" y="0" width={w} height={h} fill={OVERLAY_COLOR} />

          <g transform={centerTransform}>
            <path
              d={starPath}
              fill="none"
              stroke={GLOW_OUTER}
              strokeWidth={44}
              filter="url(#lo-halo-outer)"
              opacity="0.65"
            />
            <path
              d={starPath}
              fill="none"
              stroke={GLOW_INNER}
              strokeWidth={18}
              filter="url(#lo-halo-inner)"
              opacity="0.95"
            />
          </g>
          {SATELLITES.map((s, i) => (
            <g key={i} transform={satTransform(s)}>
              <path
                d={starPath}
                fill="none"
                stroke={GLOW_OUTER}
                strokeWidth={44}
                filter="url(#lo-halo-outer)"
                opacity="0.55"
              />
              <path
                d={starPath}
                fill="none"
                stroke={GLOW_INNER}
                strokeWidth={18}
                filter="url(#lo-halo-inner)"
                opacity="0.85"
              />
            </g>
          ))}
        </g>
      </svg>

    </div>
  );
}